import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Check, Lock, Eye } from 'lucide-react';
import { STAT_CHIP } from '../ui/StatCard';
import { BEHAVIOUR_TAGS } from '../../utils/behaviourTags';
import { movementSense, formatDelta } from '../../utils/simulationEngine';

/**
 * One decision card.
 *
 * `reveal` is the redaction boundary made visible. When false the card renders ONLY
 * what a participant is allowed to see: title and description. whenChosen,
 * leadershipAngle, behaviour and kpi are debrief-only.
 *
 * In phase 1 this component enforces it by not rendering. From phase 3 the SERVER
 * strips those fields before they reach a participant socket — this prop is then a
 * second line of defence, not the only one. Do not let it become the only one.
 */
const DecisionCard = ({
  card,
  kpis,
  selected = false,
  locked = false,
  reveal = false,
  disabled = false,
  onSelect,
  compact = false,
}) => {
  const interactive = typeof onSelect === 'function' && !disabled;

  return (
    <button
      type="button"
      onClick={interactive ? () => onSelect(card.id) : undefined}
      disabled={!interactive}
      aria-pressed={selected}
      className={clsx(
        'group w-full rounded-2xl border text-left transition-all',
        compact ? 'p-3' : 'p-4',
        selected
          ? 'border-primary-500 bg-primary-50/70 ring-2 ring-primary-500/30 dark:border-primary-400 dark:bg-primary-500/10'
          : 'border-neutral-200 bg-white hover:border-neutral-300 dark:border-neutral-700/60 dark:bg-neutral-900 dark:hover:border-neutral-600',
        interactive && 'cursor-pointer hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-primary-400',
        !interactive && 'cursor-default',
        disabled && 'opacity-60'
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={clsx(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold',
            selected
              ? 'bg-primary-500 text-white'
              : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400'
          )}
        >
          {selected ? <Check size={13} strokeWidth={3} /> : card.order}
        </span>

        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold leading-snug text-neutral-900 dark:text-neutral-100">
            {card.title}
          </h4>
          <p className="mt-0.5 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
            {card.description}
          </p>

          {/* ---------- everything below is DEBRIEF-ONLY ---------- */}
          {reveal && (
            <div className="mt-3 space-y-2.5 border-t border-dashed border-neutral-200 pt-3 dark:border-neutral-700">
              {card.whenChosen && (
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                    When people choose it
                  </span>
                  <p className="mt-0.5 text-xs text-neutral-600 dark:text-neutral-300">{card.whenChosen}</p>
                </div>
              )}

              {card.leadershipAngle && (
                <div className="rounded-lg bg-neutral-50 px-2.5 py-2 dark:bg-neutral-800/60">
                  <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                    <Eye size={10} strokeWidth={2.5} />
                    Leadership angle
                  </span>
                  <p className="mt-0.5 text-xs font-medium text-neutral-800 dark:text-neutral-100">
                    {card.leadershipAngle}
                  </p>
                </div>
              )}

              {card.behaviour?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {card.behaviour.tags.map((tag) => {
                    const meta = BEHAVIOUR_TAGS[tag];
                    if (!meta) return null;
                    return (
                      <span
                        key={tag}
                        className={clsx(
                          'rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
                          STAT_CHIP[meta.variant] || STAT_CHIP.slate
                        )}
                      >
                        {meta.label}
                      </span>
                    );
                  })}
                </div>
              )}

              {kpis && card.kpi && (
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {kpis.map((kpi) => {
                    const d = card.kpi[kpi.key];
                    const sense = movementSense(kpi, d);
                    return (
                      <span key={kpi.key} className="flex items-baseline gap-1 text-[11px]">
                        <span className="text-neutral-400">{kpi.label}</span>
                        <span
                          className={clsx(
                            'font-semibold tabular-nums',
                            sense === 'good' && 'text-emerald-600 dark:text-emerald-400',
                            sense === 'bad' && 'text-red-600 dark:text-red-400',
                            sense === 'neutral' && 'text-neutral-400'
                          )}
                        >
                          {formatDelta(d)}
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {locked && (
          <Lock size={13} strokeWidth={2} className="mt-1 shrink-0 text-neutral-300 dark:text-neutral-600" />
        )}
      </div>
    </button>
  );
};

DecisionCard.propTypes = {
  card: PropTypes.object.isRequired,
  kpis: PropTypes.arrayOf(PropTypes.object),
  selected: PropTypes.bool,
  locked: PropTypes.bool,
  reveal: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  compact: PropTypes.bool,
};

export default DecisionCard;
